import { useState, useEffect, useCallback, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { listGroups, getErrorMessage } from '../../api/groups';
import { useAuth } from '../../context/AuthContext';
import { getAppIcon } from './appIcons';
import { getAppColor } from './appColors';

const SESSION_LABELS = {
  initiation: 'Initiation',
  programmation: 'Programmation',
  simulation: 'Simulation',
  cloture: 'Clôture',
};

const SESSION_ORDER = ['initiation', 'programmation', 'simulation', 'cloture'];

// Tableau de bord : vue d'ensemble de la progression de tous les groupes,
// répartition par étape et par application, dans un style épuré (cartes, chiffres clés).
export default function GroupsDashboard() {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    listGroups(token)
      .then(setGroups)
      .catch((err) => setError(getErrorMessage(err, 'Impossible de charger les groupes.')))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = groups.length;
    const avgProgress = total
      ? Math.round(groups.reduce((sum, g) => sum + g.progress, 0) / total)
      : 0;
    const completed = groups.filter((g) => g.progress === 100).length;
    const notStarted = groups.filter((g) => g.progress === 0).length;

    const bySessionType = SESSION_ORDER.map((type) => ({
      name: SESSION_LABELS[type],
      value: groups.filter((g) => g.session_type === type).length,
      type,
    })).filter((s) => s.value > 0);

    const sessionColors = {
      initiation: '#5b8def',
      programmation: '#f2994a',
      simulation: '#9b6bea',
      cloture: '#2f9e6e',
    };

    return { total, avgProgress, completed, notStarted, bySessionType, sessionColors };
  }, [groups]);

  if (loading) return <p className="admin-empty">Chargement…</p>;
  if (error) return <div className="auth-error">{error}</div>;
  if (groups.length === 0) return <p className="admin-empty">Aucun groupe créé pour le moment.</p>;

  return (
    <div>
      {/* ── Cartes de stats clés ── */}
      <div className="notion-stats-grid">
        <div className="notion-stat-card">
          <div className="notion-stat-value">{stats.total}</div>
          <div className="notion-stat-label">Groupes actifs</div>
        </div>
        <div className="notion-stat-card">
          <div className="notion-stat-value">{stats.avgProgress}%</div>
          <div className="notion-stat-label">Progression moyenne</div>
        </div>
        <div className="notion-stat-card">
          <div className="notion-stat-value">{stats.completed}</div>
          <div className="notion-stat-label">Terminés (100%)</div>
        </div>
        <div className="notion-stat-card">
          <div className="notion-stat-value">{stats.notStarted}</div>
          <div className="notion-stat-label">Pas encore démarrés</div>
        </div>
      </div>

      <div className="notion-dashboard-row">
        {/* ── Répartition par étape (donut) ── */}
        <div className="notion-card notion-card-donut">
          <h3 className="notion-card-title">Répartition par étape</h3>
          <div className="notion-donut-wrapper">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={stats.bySessionType}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={3}
                >
                  {stats.bySessionType.map((entry) => (
                    <Cell key={entry.type} fill={stats.sessionColors[entry.type]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="notion-legend">
            {stats.bySessionType.map((s) => (
              <div key={s.type} className="notion-legend-item">
                <span className="notion-legend-dot" style={{ background: stats.sessionColors[s.type] }} />
                {s.name} — {s.value}
              </div>
            ))}
          </div>
        </div>

        {/* ── Progression par groupe ── */}
        <div className="notion-card notion-card-progress">
          <h3 className="notion-card-title">Progression par groupe</h3>
          <div className="notion-progress-list">
            {groups.map((g) => (
              <div key={g.id} className="notion-progress-row">
                <div className="notion-progress-info">
                  <span className="notion-progress-icon">{getAppIcon(g.application)}</span>
                  <div>
                    <div className="notion-progress-name">{g.name}</div>
                    <div className="notion-progress-meta">
                      {g.application_label} · {SESSION_LABELS[g.session_type]}
                    </div>
                  </div>
                </div>
                <div className="notion-progress-bar-wrapper">
                  <div className="notion-progress-bar-track">
                    <div
                      className="notion-progress-bar-fill"
                      style={{ width: `${g.progress}%`, background: getAppColor(g.application) }}
                    />
                  </div>
                  <span className="notion-progress-pct">{g.progress}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}